import { lazy, Suspense, type ComponentType, type ReactNode } from 'react'
import { createBrowserRouter, Navigate, useSearchParams } from 'react-router-dom'

import {
  useGlobalContestsCatalog,
  useGlobalTasksCatalog,
  useGlobalWorksCatalog,
} from '@/features/platform-catalog/hooks/useGlobalCatalogBrowse'
import RootLayout from '@/shared/layouts/RootLayout'
import { RouteFallback } from '@/shared/components/RouteFallback'

const HomePage = lazy(() => import('@/features/home/pages/HomePage'))
const HomeFeaturesPage = lazy(() => import('@/features/home/pages/HomeFeaturesPage'))
const ContestsPage = lazy(() => import('@/features/contests/pages/ContestsPage'))
const PlatformContestsPage = lazy(() => import('@/features/platform-catalog/pages/PlatformContestsPage'))
const TasksPage = lazy(() => import('@/features/tasks/pages/TasksPage'))
const LearningPage = lazy(() => import('@/features/learning/pages/LearningPage'))
const PlatformTasksPage = lazy(() => import('@/features/platform-catalog/pages/PlatformTasksPage'))
const WorksPage = lazy(() => import('@/features/works/pages/WorksPage'))
const WorksWatchPage = lazy(() => import('@/features/works/pages/WorksWatchPage'))
const PlatformWorksPage = lazy(() => import('@/features/platform-catalog/pages/PlatformWorksPage'))
const AccountWorksPage = lazy(() => import('@/features/account/pages/AccountWorksPage'))
const AccountRevenuePage = lazy(() => import('@/features/account/pages/AccountRevenuePage'))
const AccountDashboardPage = lazy(() => import('@/features/account/pages/AccountDashboardPage'))
const AccountRechargePage = lazy(() => import('@/features/account/pages/AccountRechargePage'))
const AccountWithdrawalsPage = lazy(() => import('@/features/account/pages/AccountWithdrawalsPage'))
const CopyrightPage = lazy(() => import('@/features/copyright/pages/CopyrightPage'))
const MarketplacePage = lazy(() => import('@/features/marketplace/pages/MarketplacePage'))
const SubscriptionPage = lazy(() => import('@/features/subscription/pages/SubscriptionPage'))
const CreatorProfilePage = lazy(() => import('@/features/creators/pages/CreatorProfilePage'))
const AccountProfilePage = lazy(() => import('@/features/account/pages/AccountProfilePage'))
const AccountSecurityPage = lazy(() => import('@/features/account/pages/AccountSecurityPage'))
const AccountFeedbackPage = lazy(() => import('@/features/account/pages/AccountFeedbackPage'))
const AccountStoragePage = lazy(() => import('@/features/account/pages/AccountStoragePage'))
const AccountLayout = lazy(() => import('@/features/account/layouts/AccountLayout'))
const AuthLayout = lazy(() => import('@/features/auth/layouts/AuthLayout'))
const LoginPage = lazy(() => import('@/features/auth/pages/LoginPage'))
const RegisterPage = lazy(() => import('@/features/auth/pages/RegisterPage'))
const ForgotPasswordPage = lazy(() => import('@/features/auth/pages/ForgotPasswordPage'))
const StudioApp = lazy(() => import('@/features/studio/StudioApp'))
const MessagesPage = lazy(() => import('@/features/messages/pages/MessagesPage'))

function PlatformAwareRoute({
  tenant: TenantPage,
  catalog: CatalogPage,
  useCatalog,
}: {
  tenant: ComponentType
  catalog: ComponentType
  useCatalog: () => boolean
}) {
  const showCatalog = useCatalog()
  const Page = showCatalog ? CatalogPage : TenantPage
  return (
    <Suspense fallback={<RouteFallback />}>
      <Page />
    </Suspense>
  )
}

function withSuspense(node: ReactNode) {
  return <Suspense fallback={<RouteFallback />}>{node}</Suspense>
}

function ContestsPageSwitch() {
  const [params] = useSearchParams()
  const idStr = params.get('id')
  const hasDetail = idStr != null && Number.isFinite(Number(idStr))
  const showCatalog = useGlobalContestsCatalog()

  if (hasDetail) {
    return withSuspense(<ContestsPage />)
  }
  return (
    <PlatformAwareRoute
      tenant={ContestsPage}
      catalog={PlatformContestsPage}
      useCatalog={() => showCatalog}
    />
  )
}

function TasksPageSwitch() {
  const [params] = useSearchParams()
  const idStr = params.get('id')
  const tab = params.get('tab')
  const hasDetail = idStr != null && Number.isFinite(Number(idStr))
  const showCatalog = useGlobalTasksCatalog()

  if (hasDetail || tab === 'my') {
    return withSuspense(<TasksPage />)
  }
  return (
    <PlatformAwareRoute
      tenant={TasksPage}
      catalog={PlatformTasksPage}
      useCatalog={() => showCatalog}
    />
  )
}

function WorksPageSwitch() {
  const [params] = useSearchParams()
  const id = params.get('id')
  const showCatalog = useGlobalWorksCatalog()

  if (id) {
    return withSuspense(<WorksPage />)
  }
  return (
    <PlatformAwareRoute
      tenant={WorksPage}
      catalog={PlatformWorksPage}
      useCatalog={() => showCatalog}
    />
  )
}

/** 旧画布/制剧链接 → 创作中心 in-app */
function LegacyStudioRedirect() {
  return <Navigate to="/studio" replace />
}

function StudioRoute() {
  return withSuspense(<StudioApp />)
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    children: [
      { index: true, element: withSuspense(<HomePage />) },
      { path: 'capabilities', element: withSuspense(<HomeFeaturesPage />) },
      { path: 'studio/*', element: <StudioRoute /> },
      { path: 'projects', element: <LegacyStudioRedirect /> },
      { path: 'projects/*', element: <LegacyStudioRedirect /> },
      { path: 'production', element: <LegacyStudioRedirect /> },
      { path: 'production/*', element: <LegacyStudioRedirect /> },
      { path: 'workbench', element: <LegacyStudioRedirect /> },
      { path: 'aistudio', element: <LegacyStudioRedirect /> },
      { path: 'works', element: withSuspense(<WorksPageSwitch />) },
      { path: 'works/watch', element: withSuspense(<WorksWatchPage />) },
      {
        path: 'account',
        element: withSuspense(<AccountLayout />),
        children: [
          { index: true, element: <Navigate to="/account/dashboard" replace /> },
          { path: 'dashboard', element: withSuspense(<AccountDashboardPage />) },
          { path: 'works', element: withSuspense(<AccountWorksPage />) },
          { path: 'revenue', element: withSuspense(<AccountRevenuePage />) },
          { path: 'recharge', element: withSuspense(<AccountRechargePage />) },
          { path: 'withdrawals', element: withSuspense(<AccountWithdrawalsPage />) },
          { path: 'storage', element: withSuspense(<AccountStoragePage />) },
          { path: 'profile', element: withSuspense(<AccountProfilePage />) },
          { path: 'security', element: withSuspense(<AccountSecurityPage />) },
          { path: 'feedback', element: withSuspense(<AccountFeedbackPage />) },
        ],
      },
      { path: 'contests', element: withSuspense(<ContestsPageSwitch />) },
      { path: 'copyright', element: withSuspense(<CopyrightPage />) },
      { path: 'marketplace', element: withSuspense(<MarketplacePage />) },
      { path: 'marketplace/:publicId', element: withSuspense(<MarketplacePage />) },
      { path: 'tasks', element: withSuspense(<TasksPageSwitch />) },
      { path: 'learning', element: withSuspense(<LearningPage />) },
      { path: 'subscription', element: withSuspense(<SubscriptionPage />) },
      { path: 'messages', element: withSuspense(<MessagesPage />) },
      { path: 'creators/:id', element: withSuspense(<CreatorProfilePage />) },
      { path: 'u/:username', element: withSuspense(<CreatorProfilePage />) },
      {
        path: 'auth',
        element: withSuspense(<AuthLayout />),
        children: [
          { index: true, element: <Navigate to="/auth/login" replace /> },
          { path: 'login', element: withSuspense(<LoginPage />) },
          { path: 'register', element: withSuspense(<RegisterPage />) },
          { path: 'forgot-password', element: withSuspense(<ForgotPasswordPage />) },
        ],
      },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
])
